<template>
  <card>
    <div slot="header" class="d-flex justify-content-between">
      <h4 class="card-title">{{ $t("message.profile") }}</h4>
      <div v-if="isForm">
        <n-button type="secondary" simple link @click.native="cancel">
          {{ $t("message.cancel") }}
        </n-button>
        <n-button type="primary" native-type="submit" form="profile-form">
          {{ $t("message.save") }}
        </n-button>
      </div>
      <n-button v-else type="secondary" simple @click.native="edit">
        {{ $t("message.edit") }}
      </n-button>
    </div>
    <ValidationObserver v-slot="{ handleSubmit }">
      <form id="profile-form" @submit.prevent="handleSubmit(submit)">
        <div class="row">
          <div class="col-12 col-md-6">
            <span class="text-muted">{{ $t("message.userName") }}</span>
          </div>
          <div class="col-12 col-md-6">
            <validation-provider
              v-if="isForm"
              rules="required|min:2"
              v-slot="{ errors }"
            >
              <fg-input
                class="mb-0"
                v-model="model.displayName"
                type="text"
                :placeholder="$t('message.userNamePlaceholder')"
                :error="errors[0]"
              />
            </validation-provider>
            <span v-else>{{ userToken.displayName }}</span>
          </div>
        </div>
        <hr />
        <div class="row">
          <div class="col-12 col-md-6">
            <span class="text-muted">
              {{ $t("message.emailAddress") }}
            </span>
          </div>
          <div class="col-12 col-md-6">
            <validation-provider
              v-if="isForm"
              rules="required|email"
              v-slot="{ errors }"
            >
              <fg-input
                class="mb-0"
                v-model="model.email"
                type="text"
                :placeholder="$t('message.userEmailAddressPlaceholder')"
                :error="errors[0]"
              />
            </validation-provider>
            <span v-else>{{ userToken.email }}</span>
          </div>
        </div>
        <hr />
        <div class="row">
          <div class="col-12 col-md-6">
            <span class="text-muted">
              {{ $t("message.password") }}
            </span>
          </div>
          <div class="col-12 col-md-6">
            <n-button
              class="m-0"
              type="danger"
              v-if="isForm"
              simple
              @click.native="showChangePassword"
            >
              {{ $t("message.changePassword") }}
            </n-button>
            <span v-else>********</span>
          </div>
        </div>
        <br />
      </form>
    </ValidationObserver>
    <modal
      :show.sync="isConfirmPasswordVisible"
      headerClasses="justify-content-center"
    >
      <h4 slot="header" class="title title-up">
        {{ $t("message.confirmPassword") }}
      </h4>
      <form id="confirm-password-form" @submit.prevent="confirmPassword">
        <fg-input
          id="password"
          v-model="password"
          type="password"
          :placeholder="$t('message.passwordPlaceholder')"
          autofocus
        />
      </form>
      <template slot="footer">
        <n-button @click.native="cancelConfirmPassword" round link>{{
          $t("message.cancel")
        }}</n-button>
        <n-button
          type="primary"
          native-type="submit"
          form="confirm-password-form"
          :busy="isSaving"
          >{{ $t("message.continue") }}</n-button
        >
      </template>
    </modal>
    <ValidationObserver ref="changePasswordObserver" v-slot="{ handleSubmit }">
      <modal
        :show.sync="isChangePasswordVisible"
        headerClasses="justify-content-center"
      >
        <h4 slot="header" class="title title-up">
          {{ $t("message.changePassword") }}
        </h4>
        <form
          id="change-password-form"
          @submit.prevent="handleSubmit(submitChangePassword)"
        >
          <ValidationProvider name="password" rules="min:8" v-slot="{ errors }">
            <fg-input
              id="existingPassword"
              type="password"
              :label="$t('message.existingPassword')"
              v-model="password"
              :error="errors[0]"
            />
          </ValidationProvider>
          <ValidationProvider
            name="newPassword"
            rules="required|min:8"
            v-slot="{ errors }"
          >
            <fg-input
              type="password"
              :label="$t('message.newPassword')"
              v-model="newPassword"
              :error="errors[0]"
            />
          </ValidationProvider>
          <ValidationProvider
            rules="required|password:@newPassword"
            v-slot="{ errors }"
          >
            <fg-input
              type="password"
              :label="$t('message.confirmPassword')"
              v-model="confirmNewPassword"
              :error="errors[0]"
            />
          </ValidationProvider>
        </form>
        <template slot="footer">
          <n-button @click.native.prevent="cancelChangePassword" link>{{
            $t("message.cancel")
          }}</n-button>
          <n-button
            type="primary"
            native-type="submit"
            form="change-password-form"
            :busy="isSaving"
            >{{ $t("message.continue") }}</n-button
          >
        </template>
      </modal>
    </ValidationObserver>
  </card>
</template>
<script>
import { mapGetters, mapActions } from "vuex";
import _ from "lodash";
import { Modal } from "@/components";

export default {
  components: {
    Modal
  },
  data() {
    return {
      loader: null,
      isForm: false,
      isSaving: false,
      model: null,
      isConfirmPasswordVisible: false,
      password: null,
      isChangePasswordVisible: false,
      newPassword: null,
      confirmNewPassword: null
    };
  },
  methods: {
    ...mapActions({
      updateUser: "updateUser",
      changePassword: "changePassword"
    }),
    edit() {
      this.setModel(_.cloneDeep(this.userToken));
      this.showForm();
    },
    cancel() {
      this.hideForm();
      this.hideSavingIndicator();
      this.hideConfirmPassword();
      this.setModel(null);
    },
    submit() {
      this.showConfirmPassword();
    },
    async confirmPassword() {
      this.showSavingIndicator();

      const { model, password } = this;
      const user = {
        ...model,
        password
      };

      try {
        await this.updateUser(user);
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          text: this.$t("message.saveSuccess")
        });
        this.hideConfirmPassword();
        this.hideForm();
        this.setModel(null);
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text: this.$t("message.saveFailure") + ` ${error.message}`
        });
        this.hideConfirmPassword();
      } finally {
        this.resetPassword();
        this.hideSavingIndicator();
      }
    },
    setModel(model) {
      this.model = model;
    },
    showForm() {
      this.isForm = true;
    },
    hideForm() {
      this.isForm = false;
    },
    showSavingIndicator() {
      this.isSaving = true;
    },
    hideSavingIndicator() {
      this.isSaving = false;
    },
    cancelConfirmPassword() {
      this.hideConfirmPassword();
      this.resetPassword();
    },
    showConfirmPassword() {
      this.isConfirmPasswordVisible = true;
      this.$nextTick(this.focusPasswordInput);
    },
    hideConfirmPassword() {
      this.isConfirmPasswordVisible = false;
    },
    async submitChangePassword() {
      this.showSavingIndicator();
      const { password, newPassword } = this;

      try {
        await this.changePassword({
          password,
          newPassword
        });
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          text: this.$t("message.passwordResetSuccess")
        });
        this.hideChangePassword();
        this.resetPasswords();
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text: error.message
        });
      } finally {
        this.hideSavingIndicator();
      }
    },
    cancelChangePassword() {
      this.hideChangePassword();
      this.resetPasswords();
    },
    showChangePassword() {
      this.isChangePasswordVisible = true;
      this.$nextTick(this.focusExistingPasswordInput);
    },
    hideChangePassword() {
      this.isChangePasswordVisible = false;
      this.$nextTick(this.resetChangePasswordValidation);
    },
    resetChangePasswordValidation() {
      this.$refs.changePasswordObserver.reset();
    },
    resetPasswords() {
      this.resetPassword();
      this.newPassword = null;
      this.confirmNewPassword = null;
    },
    resetPassword() {
      this.password = null;
    },
    focusPasswordInput() {
      document.getElementById("password").focus();
    },
    focusExistingPasswordInput() {
      document.getElementById("existingPassword").focus();
    }
  },
  computed: mapGetters(["userToken"])
};
</script>
